<template>
  <!-- the FI-1432 iOS-Messages notification card list, ported from navbar's
       profile dropdown and styled by its global .nc-msg-* rules on purpose so
       every surface stays pixel-identical. rendered by fi_top_nav in two
       places: the desktop bell popover and the mobile account panel. -->
  <div class="nc-msg-section">
    <div class="nc-msg-header">
      <span class="nc-msg-title">Notifications</span>
      <b-button
        v-if="unreadCount > 0"
        size="sm"
        variant="outline-secondary"
        class="nc-msg-mark-all-btn"
        @click.stop="handleMarkAllRead"
      >
        <i class="fa fa-check-circle-o" aria-hidden="true"></i>
        Mark all read
      </b-button>
    </div>
    <div v-if="!notifications.length" class="nc-msg-empty">
      <i class="fa fa-bell-o" aria-hidden="true"></i>
      <div>No notifications yet</div>
    </div>
    <div v-else class="nc-msg-list">
      <a v-for="n in notifications.slice(0, 5)"
         :key="n.notification_id"
         :href="n.link || '#'"
         class="nc-msg-card"
         :class="{ 'nc-msg-card-unread': !n.read_at, ['nc-msg-card-' + typeStyle(n.type)]: true }"
         @click.stop.prevent="handleNotificationClick(n)">
        <span class="nc-msg-puck">
          <svg v-if="n.type === 'report_complete'" class="nc-puck-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <path d="M14 2v6h6"/>
            <polyline points="8 14 11 17 16 12"/>
          </svg>
          <template v-else>
            <span v-if="_isSvgIconWrap(iconForNotificationRow(n))" class="ni-svg" v-html="_svgIconHtmlWrap(iconForNotificationRow(n))" aria-hidden="true"></span>
            <i v-else class="fa" :class="iconForNotificationRow(n)" aria-hidden="true"></i>
          </template>
        </span>
        <div class="nc-msg-body">
          <div class="nc-msg-row-top">
            <span class="nc-msg-heading">{{ n.title }}</span>
            <span class="nc-msg-time">{{ relativeTime(n.created_at) }}</span>
          </div>
          <div v-if="n.body_preview" class="nc-msg-preview" v-html="n.body_preview"></div>
        </div>
      </a>
    </div>
    <div v-if="notifications.length" class="nc-msg-footer">
      <b-button
        :to="{ name: 'notifications_list' }"
        variant="outline-primary"
        size="sm"
        block
        class="nc-msg-see-all-btn"
        @click.native="$emit('closed')"
      >
        <span>See all notifications</span>
        <i class="fa fa-angle-right" aria-hidden="true"></i>
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  mark_all_notifications_read as cm_mark_all_notifications_read,
  record_notification_action as cm_record_notification_action,
} from '@/networking/apis'
import {
  tbn_mark_all_notifications_read,
  tbn_record_notification_action,
} from '@/networking/tbn_apis'
import { iconForNotification, isSvgIcon, customSvgHtml } from '@/utils/notification_icons'
import { parseSupportTicketLink, openSupportTicket } from '@/utils/support_ticket_links'

export default {
  name: 'fi_notification_list',
  computed: {
    ...mapState({
      user: state => state.profile.user,
      unreadCount: state => state.notifications.unreadCount,
      notifications: state => state.notifications.notifications,
    }),
    isTbn() {
      const u = this.user || {}
      return !!(u.types && u.types[u.tenant] === 'TBN')
    },
  },
  methods: {
    iconForNotificationRow(n) {
      return iconForNotification(n)
    },
    _isSvgIconWrap(name) {
      return isSvgIcon(name)
    },
    _svgIconHtmlWrap(name) {
      return customSvgHtml(name)
    },
    typeStyle(type) {
      if (type === 'announcement') return 'announcement'
      if (type === 'report_complete') return 'report'
      if (type === 'service_alert') return 'service-alert'
      if (type === 'service_resolved') return 'service-resolved'
      if (type === 'support_comment') return 'support'
      return 'generic'
    },
    relativeTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const diff = Math.max(0, Date.now() - d.getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
      if (diff < 604800) return Math.floor(diff / 86400) + 'd ago'
      return d.toLocaleDateString()
    },
    async handleMarkAllRead() {
      this.$store.commit('notifications/mark_all_read_local')
      try {
        const fn = this.isTbn ? tbn_mark_all_notifications_read : cm_mark_all_notifications_read
        await fn(this.user.tenant)
      } catch (_) { /* best effort; next poll will reconcile */ }
    },
    async handleNotificationClick(n) {
      if (!n || !n.notification_id) return
      this.$emit('closed')
      this.$store.commit('notifications/apply_action_local', n.notification_id)
      try {
        const fn = this.isTbn ? tbn_record_notification_action : cm_record_notification_action
        await fn(this.user.tenant, n.notification_id)
      } catch (_) { /* best effort; next poll will reconcile */ }
      const dest = (n.link || '').trim()
      // a support ticket opens in place, over whatever the viewer was doing
      const ticket = parseSupportTicketLink(dest)
      if (ticket) {
        openSupportTicket(this, ticket)
        return
      }
      if (dest && dest !== '/') {
        try {
          if (dest.startsWith('http')) {
            window.location.href = dest
          } else {
            this.$router.push(dest)
          }
        } catch (_) { /* router may reject if already on this route */ }
      }
    },
  },
}
</script>
